"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogOverlay,
} from "@/components/atoms/Dialog";
// import DynamicIcon from "@/components/atoms/DynamicIcon";
import { getYouTubeEmbedUrl } from "@/utils/video";
import { cn } from "@/lib/utils";
// Pixel-identical local copy — see assets/uiGlyphs for why.
import { Cross2Icon } from "@/assets/uiGlyphs";

interface YouTubeVideoOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  videoTitle?: string;
  onVideoModalClosed?: () => void;
}

const YouTubeVideoOverlay = ({
  isOpen,
  onClose,
  videoUrl,
  title = "Video Player",
  videoTitle,
  onVideoModalClosed,
}: YouTubeVideoOverlayProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [embedUrl, setEmbedUrl] = useState("");

  // Update embed URL when dialog opens or videoUrl changes
  useEffect(() => {
    if (isOpen && videoUrl) {
      setEmbedUrl(getYouTubeEmbedUrl(videoUrl, true, false));
    } else {
      // Clear the URL when closed to stop video playback
      setEmbedUrl("");
    }
  }, [isOpen, videoUrl]);

  // Handle video stopping when overlay closes
  useEffect(() => {
    if (!isOpen) {
      // Clear iframe src to stop video playback
      if (iframeRef.current) {
        iframeRef.current.src = "";
      }
      if (onVideoModalClosed) {
        onVideoModalClosed();
      }
    }
  }, [isOpen, onVideoModalClosed]);

  const handleClose = useCallback(() => {
    if (onVideoModalClosed) {
      onVideoModalClosed();
    }
    onClose();
  }, [onClose, onVideoModalClosed]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogOverlay className="bg-gray-900/90" />

      <DialogContent className="max-w-4xl w-[90vw] p-0 bg-black-100 rounded-lg">
        {/* Inside the content, not beside it: Radix renders the title as an <h2>, and out
            here it sat in the page itself — an empty <h2> on every page using this, open
            or not. In the content it exists only while the dialog is open. */}
        <DialogTitle className="sr-only">{videoTitle || title}</DialogTitle>
        <div className="relative">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close video"
            className={cn(
              "absolute z-50 flex size-10 cursor-pointer items-center justify-center rounded-full",
              "border border-white bg-black text-white shadow-none",
              "transition-colors hover:bg-zinc-950",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
              /* Sits on the corner, slightly past the player edge (matches reference modal) */
              "-right-2 -top-2 sm:-right-3 sm:-top-3",
            )}
          >
            <Cross2Icon className="w-4 h-4" />
          </button>
          {embedUrl && (
            <div className="aspect-video w-full">
              <iframe
                ref={iframeRef}
                width="100%"
                height="100%"
                src={embedUrl}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default YouTubeVideoOverlay;
